import { type PublishedStream } from 'FrontRoyalStore';
import moize from 'moize';
import transformKeyCase from 'Utils/transformKeyCase';
import { type LessonProgress, type StreamProgress } from 'ContentProgress';
import { type CurrentUser } from 'Users';
import { getCohort, getProgramInclusion } from 'Users/BaseUser';
import { streamIsManuallyUnlocked } from 'ProgramInclusion';
import { getPeriodDates, getPeriodIndexForRequiredLocalePackId } from 'Cohorts';
import { angularInjectorProvider } from 'Injector';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import {
    type StreamSnakeCasedAttrs,
    type Stream,
    type Lesson,
    type StreamCompoundItemVersionKey,
    type StreamForDashboard,
    type LessonForDashboard,
    type StreamForDashboardSnakeCasedAttrs,
} from './Lessons.types';
import { lessonToIguanaAttrs } from './lesson';

export function camelCaseStream(stream: StreamForDashboardSnakeCasedAttrs): StreamForDashboard;
export function camelCaseStream(stream: PublishedStream | StreamSnakeCasedAttrs): Stream;
export function camelCaseStream(
    stream: PublishedStream | StreamSnakeCasedAttrs | StreamForDashboardSnakeCasedAttrs,
): Stream | StreamForDashboard {
    const camelCased = transformKeyCase(stream, { to: 'camelCase' }) as Stream | StreamForDashboard;

    // localePackId is a prototype getter on Iguana objects that's lost during asJson(). Derive it from localePack.
    if ('localePack' in camelCased && camelCased.localePack) {
        Object.assign(camelCased, { localePackId: camelCased.localePack.id });
    }

    return camelCased;
}

const computeOrderedLessons = (stream: StreamForDashboard | Stream<boolean>) =>
    stream.chapters.flatMap(chapter =>
        chapter.lessonIds
            .map(lessonId => {
                const lesson = stream.lessons.find(_ => _.id === lessonId);
                if (!lesson) {
                    const ErrorLogService =
                        angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
                    if (ErrorLogService) {
                        ErrorLogService.notifyInProd('Chapter references missing lesson', null, {
                            level: 'warning',
                            stream_id: stream.id,
                            stream_title: stream.title,
                            chapter_title: chapter.title,
                            orphan_lesson_id: lessonId,
                        });
                    }
                    return null;
                }
                return lesson;
            })
            .filter(Boolean),
    );

const getOrderedLessonsFromDashboardStream = moize((stream?: StreamForDashboard): LessonForDashboard[] => {
    if (!stream) return [];
    return computeOrderedLessons(stream) as LessonForDashboard[];
});

const getOrderedLessonsFromFullStream = moize(<T extends boolean>(stream?: Stream<T>): Lesson<T>[] => {
    if (!stream) return [];
    return computeOrderedLessons(stream) as Lesson<T>[];
});

const isDashboardStream = (stream: StreamForDashboard | Stream<boolean>): stream is StreamForDashboard => {
    const sampleLesson = stream.lessons?.[0];
    return !sampleLesson || !('itemVersionKey' in sampleLesson);
};

export function getOrderedLessonsFromStream<T extends boolean>(stream?: Stream<T>): Lesson<T>[];
export function getOrderedLessonsFromStream(stream?: StreamForDashboard): LessonForDashboard[];
export function getOrderedLessonsFromStream(
    stream?: StreamForDashboard | Stream<boolean>,
): LessonForDashboard[] | Lesson<boolean>[] {
    if (!stream) return [];
    if (isDashboardStream(stream)) {
        return getOrderedLessonsFromDashboardStream(stream);
    }
    return getOrderedLessonsFromFullStream(stream as Stream<boolean>);
}

export const snakeCaseStream = (stream: Stream<boolean>): PublishedStream =>
    transformKeyCase(stream, { to: 'snakeCase' }) as PublishedStream;

export function getCompoundItemVersionKeyForStream(stream: Stream<boolean>): StreamCompoundItemVersionKey {
    if (!stream.publishedAt) throw new Error('Stream must have a publishedAt date');
    return {
        published_at: stream.publishedAt,
        lessons: stream.lessons.map(l => l.itemVersionKey).sort(),
        stream_lesson_tutor_bot_record_max_updated_at:
            stream.streamLessonTutorBotRecords
                ?.map(r => r.updatedAt)
                .sort()
                .reverse()[0] || null,
        schema_version: stream.schemaVersion,
    };
}

export function streamToIguanaAttrs(
    stream: Stream<false>,
    streamProgress?: StreamProgress,
    lessonProgressByLocalePackId?: Record<string, LessonProgress>,
): StreamSnakeCasedAttrs<false> {
    const lessons = stream.lessons;

    const snakeCased = transformKeyCase<StreamSnakeCasedAttrs<false>>(
        { ...stream, lessons: [], lessonStreamsProgress: streamProgress },
        {
            to: 'snakeCase',
        },
    );
    snakeCased.lessons = lessons.map(lesson =>
        lessonToIguanaAttrs(lesson, lessonProgressByLocalePackId?.[lesson.localePack?.id]),
    );

    return snakeCased;
}

export function removeLessonContent(stream: Stream<boolean>): Stream<false> {
    return {
        ...stream,
        lessons: stream.lessons.map(lessonMaybeWithFullContent => {
            const lesson = { ...lessonMaybeWithFullContent } as Lesson<false>;
            delete lesson.frames;
            return lesson as Lesson<false>;
        }),
    };
}

export const getComingSoonLessonLocalePackIds = (stream?: StreamForDashboard) => {
    if (!stream) return [];

    return stream.chapters.flatMap(chapter =>
        chapter.lessonHashes
            .map(lessonHash => {
                if (!lessonHash.comingSoon) return undefined;
                const lesson = stream.lessons.find(({ id }) => id === lessonHash.lessonId);

                if (!lesson) return undefined;

                return lesson.localePackId;
            })
            .filter(lessonLocalePackId => lessonLocalePackId !== undefined),
    );
};

export function getExamOpenTime({
    user,
    stream,
}: {
    user: CurrentUser | null;
    stream: StreamForDashboard;
}): Date | null {
    if (!stream.exam || !user) {
        return null;
    }

    const cohort = getCohort(user);
    if (!cohort) {
        return null;
    }

    if (streamIsManuallyUnlocked(getProgramInclusion(user), stream.localePackId)) {
        return null;
    }

    const periodIndex = getPeriodIndexForRequiredLocalePackId(cohort, stream.localePackId);
    if (periodIndex === -1) return null;

    const periodDates = getPeriodDates(cohort, periodIndex);
    return periodDates.startDate;
}
